<template>
  <div>
    <ValidationObserver ref="observer">
      <v-form @submit.prevent="submit">
        <h2>
          What is the legal nature of your relationship with
          {{ relations.spouse.fullName }}?
        </h2>

        <ValidationProvider
          id="marital_regime"
          v-slot="{ errors }"
          name="marital_regime"
          rules="required"
        >
          <RadioSelect
            v-if="whitelabel.omDesign"
            v-model="maritalRegime"
            :items="[
              {
                value: 'IN_COMMUNITY',
                text: 'married in community of property:',
                subLabel: `no antenuptial contract - everything is shared between you
                    and your spouse.`,
              },
              {
                value: 'OUT_OF_COMMUNITY_ACCRUAL',
                text: 'married out of community of property, with accrual:',
                subLabel: `antenuptial contract - everything you and your spouse accrue
                    after marriage is your joint property.`,
              },
              {
                value: 'OUT_OF_COMMUNITY_NO_ACCRUAL',
                text: 'married out of community of property, without accrual:',
                subLabel: `antenuptial contract - everything you and your spouse accrue
                    after marriage remains your separate property respectively.`,
              },
              {
                value: 'LIFE_PARTNER',
                text: 'permanent life partnership:',
                subLabel: `when you have signed an agreement with your partner entering
                    into a permanent life partnership.`,
              },
              {
                value: 'MUSLIM_RITES',
                text: 'marriage in terms of Muslim rites',
              },
              {
                value: 'CUSTOMARY',
                text: 'marriage in terms of Customary Law',
              },
            ]"
            :color="'white'"
            :errors="errors"
            :group="'marital_regime'"
            :flexColumn="true"
          />

          <v-radio-group
            v-else
            v-model="maritalRegime"
            name="marital_regime"
            :error-messages="errors"
          >
            <v-radio value="IN_COMMUNITY" color="primary">
              <template #label>
                <div class="marriage-type">
                  <h4 class="spouse-title text-highlight">
                    married in community of property:
                  </h4>
                  <p class="spouse-description">
                    no antenuptial contract - everything is shared between you
                    and your spouse.
                  </p>
                </div>
              </template>
            </v-radio>
            <v-radio value="OUT_OF_COMMUNITY_ACCRUAL" color="primary">
              <template #label>
                <div class="marriage-type">
                  <h4 class="spouse-title text-highlight">
                    married out of community of property,
                    <u>with</u> accrual:
                  </h4>
                  <p class="spouse-description">
                    antenuptial contract - everything you and your spouse accrue
                    after marriage is your joint property.
                  </p>
                </div>
              </template>
            </v-radio>
            <v-radio value="OUT_OF_COMMUNITY_NO_ACCRUAL" color="primary">
              <template #label>
                <div class="marriage-type">
                  <h4 class="spouse-title text-highlight">
                    married out of community of property,
                    <u>without</u> accrual:
                  </h4>
                  <p class="spouse-description">
                    antenuptial contract - everything you and your spouse accrue
                    after marriage remains your separate property respectively.
                  </p>
                </div>
              </template>
            </v-radio>
            <v-radio value="LIFE_PARTNER" color="primary">
              <template #label>
                <div class="marriage-type">
                  <h4 class="spouse-title text-highlight">
                    permanent life partnership:
                  </h4>
                  <p class="spouse-description">
                    when you have signed an agreement with your partner entering
                    into a permanent life partnership.
                  </p>
                </div>
              </template>
            </v-radio>
            <v-radio value="MUSLIM_RITES" color="primary">
              <template #label>
                <div class="marriage-type">
                  <h4 class="spouse-title text-highlight">
                    marriage in terms of Muslim rites
                  </h4>
                </div>
              </template>
            </v-radio>
            <v-radio value="CUSTOMARY" color="primary">
              <template #label>
                <div class="marriage-type">
                  <h4 class="spouse-title text-highlight">
                    marriage in terms of Customary Law
                  </h4>
                </div>
              </template>
            </v-radio>
          </v-radio-group>
        </ValidationProvider>

        <div class="button-container">
          <DefaultButton
            @defaultButtonClicked="$router.push('spouse')"
            :small="true"
          />
          <GradientButton
            :title="'Next'"
            :small="true"
            :type="'submit'"
            :callout="'right'"
            :loading="buttonLoading"
          />
        </div>
      </v-form>
    </ValidationObserver>

    <ErrorSnackbar v-model="showError" />
  </div>
</template>

<script>
  import { UPDATE_TESTATOR_MUTATION } from '../../constants/graphql/testators';
  import ErrorSnackbar from '../common/ErrorSnackbar.vue';
  import { formHelperMixin } from '../../mixins/form-helper-mixin';

  export default {
    name: 'MaritalRegime',
    components: {
      ErrorSnackbar,
    },
    mixins: [formHelperMixin],
    props: {
      testator: { type: Object, required: true },
      relations: { type: Object, required: true },
    },
    data() {
      return {
        wizard: this.$route.query.peopleWizard,
        maritalRegime: this.testator.maritalRegime,
      };
    },
    methods: {
      submit() {
        this.$refs.observer.validate().then(valid => {
          if (valid) {
            this.buttonLoading = true;
            this.updateTestator();
          } else {
            const errors = this.$refs.observer.ctx.errors;
            document
              .getElementById(
                Object.keys(errors).find(field => errors[field].length)
              )
              .scrollIntoView({ block: 'center', behavior: 'smooth' });
          }
        });
      },
      updateTestator() {
        const id = this.testator.id;
        const testatorInputDetails = {
          maritalRegime: this.maritalRegime,
        };

        this.$apollo
          .mutate({
            mutation: UPDATE_TESTATOR_MUTATION,
            variables: {
              id,
              testatorInputDetails,
            },
          })
          .then(_response => {
            this.redirectToNext();
            this.buttonLoading = false;
          })
          .catch(error => {
            this.displayError();
            throw error;
          });
      },
      redirectToNext() {
        if (this.wizard == 'spouse') {
          this.$router.push({ name: 'PeopleOverview', hash: '#spouse' });
        } else {
          this.$router.push('children');
        }
      },
    },
  };
</script>

<style lang="scss" scoped>
  .v-form {
    max-width: 580px;
  }
  .marriage-type {
    border-bottom: 1px solid #dfe2e5;

    span {
      // background-color in global.scss because of IE issue
      cursor: pointer;
      font-weight: bold;
    }
  }

  .v-radio {
    -webkit-box-align: start;
    align-items: flex-start;
  }

  .spouse-title {
    color: #282828;
    font-size: 18px;
    font-weight: 700;
    line-height: 23.4px;
    display: inline-block;
  }

  .spouse-description {
    color: #282828;
    font-size: 16px;
    font-weight: 400;
    line-height: 22px;
    text-align: left;
  }
  .button-container {
    margin-top: 40px;
    display: flex;
    gap: 20px;
  }
</style>
